import { useEffect, useRef, useState } from "react";
import { teleproApi } from "../../lib/teleproApi";

/**
 * useStatusSuggestion — Vague 3.
 *
 * Debounced (400ms) call to POST /status/suggest whenever `note` changes.
 * Returns `{status, label, confidence, matched}` or null.
 *
 * The télépro can then accept the suggestion via a click, which parent
 * component uses to `onUpdate({ status: ... })`.
 */
export function useStatusSuggestion(note, { minChars = 6 } = {}) {
  const [suggestion, setSuggestion] = useState(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!note || note.trim().length < minChars) {
      setSuggestion(null);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort?.();
        const controller = new AbortController();
        abortRef.current = controller;
        const r = await teleproApi.post("/status/suggest", { note }, { signal: controller.signal });
        if (r?.data?.status) setSuggestion(r.data);
        else setSuggestion(null);
      } catch (_) {
        // silent-fail — a suggestion that fails is a non-event for the télépro
        setSuggestion(null);
      }
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [note, minChars]);

  return suggestion;
}
